import { useState, type FormEvent } from 'react';
import {
  useCreateKey,
  useInviteMember,
  useKeys,
  useRemoveMember,
  useRevokeKey,
  useSetRole,
  useTeam,
  useUsage,
} from '../api/hooks.js';
import { Button } from '../components/Button.js';
import { Card, CardFoot } from '../components/Card.js';
import { cx, LINK, MONO, TABLE_HEAD } from '../components/classes.js';
import { Field, Select, TextInput } from '../components/Field.js';
import { EmptyState, Note, ProblemNote, SkeletonRows } from '../components/Notes.js';
import { Main, PageHeader } from '../components/PageHeader.js';
import { StatusGlyph } from '../components/StatusGlyph.js';
import { Tabs } from '../components/Tabs.js';
import { formatDate, formatRecency } from '../lib/format.js';
import type { Role } from '../lib/types.js';
import { canManage, useSessionStore } from '../store/session.js';

type SettingsTab = 'team' | 'keys' | 'usage';

const ROLE_WORD: Record<Role, string> = {
  admin: 'Admin',
  verifier: 'Verifier',
  auditor: 'Auditor (read-only)',
};
const ROLES: Role[] = ['admin', 'verifier', 'auditor'];

function TeamTab() {
  const role = useSessionStore((s) => s.role);
  const me = useSessionStore((s) => s.memberEmail);
  const team = useTeam();
  const invite = useInviteMember();
  const setRole = useSetRole();
  const remove = useRemoveMember();
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('verifier');
  const admin = canManage(role);
  const members = team.data?.members ?? [];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    invite.mutate(
      { email: email.trim(), role: inviteRole },
      { onSuccess: () => setEmail('') },
    );
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_420px] gap-7">
      <Card>
        <div
          className={cx(
            'grid grid-cols-[minmax(0,1fr)_200px_140px_90px] items-center gap-5 px-6 py-3',
            TABLE_HEAD,
          )}
        >
          <span>PERSON</span>
          <span>ROLE</span>
          <span>JOINED</span>
          <span />
        </div>
        {team.isPending ? (
          <SkeletonRows rows={3} />
        ) : (
          members.map((m) => (
            <div
              key={m.id}
              className="grid grid-cols-[minmax(0,1fr)_200px_140px_90px] items-center gap-5 border-t border-line px-6 py-4 text-[14px]"
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate font-semibold">{m.email}</span>
                <span className="text-[12px] text-mute">
                  {m.invited_by ? `invited by ${m.invited_by}` : 'owner'}
                </span>
              </span>
              {admin ? (
                <Select
                  aria-label={`Role for ${m.email}`}
                  value={m.role}
                  disabled={setRole.isPending}
                  onChange={(e) =>
                    setRole.mutate({ id: m.id, role: e.target.value as Role })
                  }
                  className="h-9 text-[13px]"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_WORD[r]}
                    </option>
                  ))}
                </Select>
              ) : (
                <span>{ROLE_WORD[m.role]}</span>
              )}
              <span className="text-mute">
                {m.accepted_at ? formatDate(m.accepted_at) : 'invited'}
              </span>
              {admin && m.email !== me ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => remove.mutate(m.id)}
                  disabled={remove.isPending}
                >
                  Remove
                </Button>
              ) : (
                <span />
              )}
            </div>
          ))
        )}
        {setRole.isError || remove.isError ? (
          <div className="border-t border-line px-6 py-4">
            <ProblemNote error={setRole.error ?? remove.error} />
          </div>
        ) : null}
        <CardFoot>
          Admins manage the team, keys and billing; verifiers issue records; auditors read
          only.
        </CardFoot>
      </Card>
      <aside>
        {admin ? (
          <form
            onSubmit={submit}
            className="flex flex-col gap-5 rounded-card border border-line bg-surface p-6 shadow-card"
          >
            <span className="text-[15px] font-semibold">Invite by email</span>
            <Field id="invite-email" label="Work email">
              <TextInput
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.co.zm"
              />
            </Field>
            <Field id="invite-role" label="Role">
              <Select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_WORD[r]}
                  </option>
                ))}
              </Select>
            </Field>
            {invite.isError ? <ProblemNote error={invite.error} /> : null}
            {invite.isSuccess ? (
              <div className="text-[13px] text-good">
                Invited — they sign in with that address.
              </div>
            ) : null}
            <Button type="submit" variant="primary" disabled={invite.isPending}>
              {invite.isPending ? 'Inviting…' : 'Invite'}
            </Button>
          </form>
        ) : (
          <Note>Only an admin can invite people or change roles.</Note>
        )}
      </aside>
    </div>
  );
}

function KeysTab() {
  const role = useSessionStore((s) => s.role);
  const keys = useKeys();
  const create = useCreateKey();
  const revoke = useRevokeKey();
  const [name, setName] = useState('');
  const [env, setEnv] = useState<'live' | 'test'>('live');
  const admin = canManage(role);
  const rows = keys.data?.keys ?? [];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate({ name: name.trim(), env }, { onSuccess: () => setName('') });
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_420px] gap-7">
      <Card>
        <div
          className={cx(
            'grid grid-cols-[minmax(0,1fr)_190px_120px_130px_120px_90px] items-center gap-5 px-6 py-3',
            TABLE_HEAD,
          )}
        >
          <span>KEY</span>
          <span>PREFIX</span>
          <span>STATUS</span>
          <span>CREATED</span>
          <span>LAST USED</span>
          <span />
        </div>
        {keys.isPending ? (
          <SkeletonRows rows={2} />
        ) : rows.length === 0 ? (
          <div className="border-t border-line">
            <EmptyState title="No API keys yet">
              Create one to call the Verify API from your own systems. The console itself
              needs none.
            </EmptyState>
          </div>
        ) : (
          rows.map((k) => (
            <div
              key={k.id}
              className="grid grid-cols-[minmax(0,1fr)_190px_120px_130px_120px_90px] items-center gap-5 border-t border-line px-6 py-4 text-[14px]"
            >
              <span className="font-semibold">{k.name}</span>
              <span className={cx(MONO, 'text-[13px]')}>
                {k.prefix}…{k.last4}
              </span>
              <span className={k.status === 'active' ? 'text-good' : 'text-mute'}>
                {k.status}
              </span>
              <span className="text-mute">{formatDate(k.created_at)}</span>
              <span className="text-mute">
                {k.last_used_at ? formatRecency(k.last_used_at) : 'never'}
              </span>
              {admin && k.status === 'active' ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => revoke.mutate(k.id)}
                  disabled={revoke.isPending}
                >
                  Revoke
                </Button>
              ) : (
                <span />
              )}
            </div>
          ))
        )}
        {revoke.isError ? (
          <div className="border-t border-line px-6 py-4">
            <ProblemNote error={revoke.error} />
          </div>
        ) : null}
        <CardFoot>
          Keys are shown once at creation and stored hashed. Test keys read cached
          registers only and never issue records.
        </CardFoot>
      </Card>
      <aside className="flex flex-col gap-5">
        {admin ? (
          <form
            onSubmit={submit}
            className="flex flex-col gap-5 rounded-card border border-line bg-surface p-6 shadow-card"
          >
            <span className="text-[15px] font-semibold">Create API key</span>
            <Field id="key-name" label="Name">
              <TextInput
                id="key-name"
                type="text"
                required
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Core banking integration"
              />
            </Field>
            <Field id="key-env" label="Environment">
              <Select
                id="key-env"
                value={env}
                onChange={(e) => setEnv(e.target.value as 'live' | 'test')}
              >
                <option value="live">Live — dira_live_</option>
                <option value="test">Test — dira_test_</option>
              </Select>
            </Field>
            {create.isError ? <ProblemNote error={create.error} /> : null}
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create API key'}
            </Button>
          </form>
        ) : (
          <Note>Only an admin can create or revoke API keys.</Note>
        )}
        {create.isSuccess ? (
          <div className="flex flex-col gap-3 rounded-card border border-line bg-surface p-6 shadow-lift">
            <div className="flex items-center gap-2.5">
              <StatusGlyph status="pass" />
              <span className="text-[15px] font-semibold">
                Copy it now — it is shown once
              </span>
            </div>
            <code
              className={cx(
                MONO,
                'break-all rounded-btn bg-sunk px-3.5 py-3 text-[13px]',
              )}
            >
              {create.data.key}
            </code>
            <button
              type="button"
              className={cx(LINK, 'w-max cursor-pointer text-[13px]')}
              onClick={() => void navigator.clipboard.writeText(create.data.key)}
            >
              Copy key
            </button>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

function UsageTab() {
  const usage = useUsage();
  if (usage.isPending)
    return (
      <SkeletonRows rows={4} className="rounded-card border border-line bg-surface" />
    );
  if (usage.isError) return <ProblemNote error={usage.error} />;
  const u = usage.data;
  const quota =
    u.limits.quota_limit === null
      ? 'unlimited'
      : `${u.limits.quota_limit} / ${u.limits.quota_window}`;
  const planWord = u.plan.charAt(0).toUpperCase() + u.plan.slice(1);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_420px] gap-7">
      <Card>
        <div className="flex items-baseline justify-between px-6 py-4">
          <span className="text-[15px] font-semibold">This month · {planWord} plan</span>
          <span className={cx(MONO, 'text-[13px] text-mute')}>
            since {formatDate(u.month.starts)}
          </span>
        </div>
        {[
          ['Records issued', String(u.month.records_issued)],
          [
            'API calls',
            `${u.month.requests}${u.limits.quota_window === 'month' && u.limits.quota_limit !== null ? ` / ${u.limits.quota_limit}` : ''}`,
          ],
          ['Rate limit', `${u.limits.rpm} / min`],
          ['Request quota', quota],
          [
            'API keys',
            `${u.keys.filter((k) => !k.console).length}${u.limits.keys_limit !== null ? ` / ${u.limits.keys_limit}` : ''}`,
          ],
        ].map(([label, value]) => (
          <div
            key={label}
            className="flex justify-between border-t border-line px-6 py-3.5 text-[14px]"
          >
            <span className="text-mute">{label}</span>
            <span className={MONO}>{value}</span>
          </div>
        ))}
        <CardFoot>
          Usage is stated in the plan's own units. Billing and plan changes arrive with
          planner 2.3 (Lipila).
        </CardFoot>
      </Card>
      <aside className="flex flex-col gap-5">
        <Card>
          <div className="px-6 py-4 text-[15px] font-semibold">
            Calls by key · this month
          </div>
          {u.keys.map((k) => (
            <div
              key={k.id}
              className="flex justify-between border-t border-line px-6 py-3 text-[14px]"
            >
              <span className={k.console ? 'text-mute' : ''}>{k.name}</span>
              <span className={MONO}>{k.requests_this_month}</span>
            </div>
          ))}
        </Card>
        <Card>
          <div className="px-6 py-4 text-[15px] font-semibold">Subscription</div>
          <div className="border-t border-line px-6 py-4 text-[14px] text-mute">
            {u.subscription
              ? `${u.subscription.plan_code} · ${u.subscription.status}${u.subscription.current_period_end ? ` · renews ${formatDate(u.subscription.current_period_end)}` : ''}`
              : 'No paid subscription — the free floor applies.'}
          </div>
        </Card>
      </aside>
    </div>
  );
}

/** Wireframe §11: team, API keys, usage & billing. */
export function Settings() {
  const [tab, setTab] = useState<SettingsTab>('team');
  const accountName = useSessionStore((s) => s.accountName);
  const plan = useSessionStore((s) => s.plan);
  return (
    <Main>
      <PageHeader
        title="Settings"
        line={`${accountName ?? ''}${plan ? ` · ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan` : ''}`}
      />
      <Tabs
        tabs={[
          { key: 'team', label: 'Team' },
          { key: 'keys', label: 'API keys' },
          { key: 'usage', label: 'Usage & billing' },
        ]}
        active={tab}
        onChange={setTab}
        label="Settings sections"
      />
      {tab === 'team' ? <TeamTab /> : null}
      {tab === 'keys' ? <KeysTab /> : null}
      {tab === 'usage' ? <UsageTab /> : null}
    </Main>
  );
}
